<script setup lang="ts">
  import type { ChatContextData } from '@/composables/useChatStream'
  import { useTranslation } from '@/composables/useTranslation'
  import { File, FolderOpen } from 'lucide-vue-next'
  import { computed } from 'vue'
  import FilePdfIcon from '@/components/icons/FilePdfIcon.vue'

  const props = defineProps<{
    contextData: ChatContextData
  }>()

  const { t } = useTranslation({
    en: {
      contextAdded: 'Context {label} added. The following answers refer to this context.',
      documentsAdded: 'Documents added to the conversation.'
    },
    de: {
      contextAdded:
        'Kontext {label} hinzugefügt. Die folgenden Antworten beziehen sich auf diesen Kontext.',
      documentsAdded: 'Dokumente zur Konversation hinzugefügt.'
    },
    fr: {
      contextAdded: 'Contexte {label} ajouté. Les réponses suivantes se rapportent à ce contexte.',
      documentsAdded: 'Documents ajoutés à la conversation.'
    }
  })

  const descriptionText = computed(() => {
    if (props.contextData.caseLabel) {
      return t('contextAdded', { label: props.contextData.caseLabel })
    }
    if (props.contextData.documents?.length) {
      return t('documentsAdded')
    }
    return ''
  })

  /** Derive a file type category from a filename extension. */
  function fileVariant(filename: string): 'pdf' | 'word' | 'other' {
    const ext = filename.split('.').pop()?.toLowerCase() ?? ''
    if (ext === 'pdf') return 'pdf'
    if (['doc', 'docx'].includes(ext)) return 'word'
    return 'other'
  }

  const docsWithVariant = computed(() =>
    (props.contextData.documents ?? []).map((doc) => ({
      ...doc,
      variant: fileVariant(doc.filename)
    }))
  )
</script>

<template>
  <div class="flex flex-col items-end gap-2">
    <!-- Case badge -->
    <div
      v-if="contextData.caseLabel"
      class="inline-flex items-center gap-2 overflow-hidden rounded-lg bg-white px-1.5 py-0.5 outline outline-1
        -outline-offset-1 outline-gray-200"
    >
      <FolderOpen
        class="h-4 w-4 shrink-0 text-gray-500"
        :stroke-width="1"
      />
      <span class="line-clamp-1 text-sm font-normal leading-5 text-gray-500 font-inter">
        {{ contextData.caseLabel }}
      </span>
    </div>

    <!-- Description text -->
    <p
      v-if="descriptionText"
      class="text-right text-xs font-normal leading-4 text-gray-400 font-inter"
    >
      {{ descriptionText }}
    </p>

    <!-- Document badges -->
    <div
      v-if="docsWithVariant.length"
      class="flex flex-wrap content-start items-center justify-end gap-2"
    >
      <div
        v-for="doc in docsWithVariant"
        :key="doc.uuid"
        class="inline-flex h-10 items-center gap-2 overflow-hidden rounded-lg bg-white px-2 outline outline-1
          -outline-offset-1 outline-gray-200"
      >
        <div
          class="flex items-center justify-start overflow-hidden rounded p-1"
          :class="{
            'bg-red-100': doc.variant === 'pdf',
            'bg-blue-100': doc.variant !== 'pdf'
          }"
        >
          <FilePdfIcon
            v-if="doc.variant === 'pdf'"
            class="h-4 w-4 text-gray-600"
          />
          <File
            v-else
            class="h-4 w-4 text-gray-600"
            :stroke-width="1.5"
          />
        </div>
        <span class="line-clamp-1 text-sm font-normal leading-5 text-gray-500 font-inter">
          {{ doc.filename }}
        </span>
      </div>
    </div>
  </div>
</template>
